import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { dataHooks } from '../../dataHooks';
import { keys } from '../../translationsKeys';
import s from './LastUsedBadge.scss';

export interface LastUsedBadgeProps {
  /**
   * Narrow controls (the 40px round social icons) carry the label centred
   * underneath rather than in a corner. It is the same full label either way —
   * never an abbreviation, never a bare dot.
   */
  compact?: boolean;
  className?: string;
}

/**
 * "Last used" marker for the authentication option the returning user picked
 * last time.
 *
 * It is a plain span rather than a design-system component. CounterBadge — what
 * this feature first used — is documented as a numeric indicator and its
 * guidelines send a label like this to Badge or Tag instead; neither of those
 * offers a floating marker that can sit off a control's edge and take the
 * preset's brand colour, which is what the design calls for. Colours are CSS
 * variables (see the stylesheet) so a preset can retune it without touching
 * this component.
 *
 * Absolutely positioned — the element it marks must carry
 * `lastUsedBadgeStyles.anchor` (or its own `position: relative`).
 */
export const LastUsedBadge: React.FC<LastUsedBadgeProps> = ({
  compact = false,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <span
      className={classNames(s.pill, compact ? s.under : s.corner, className)}
      data-hook={dataHooks.lastUsedBadge.badge}
    >
      {t(keys['lastUsed.badge.label'])}
    </span>
  );
};

export { s as lastUsedBadgeStyles };
